import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Globe, MapPin, Send, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Enquiry received. We will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full bg-[#0d0d0d] border border-white/12 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent transition-all";

  return (
    <div className="pt-40 pb-28">
      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16,1,0.3,1] }}>
            <div className="overline mb-6">Contact</div>
            <h1 className="font-display font-light text-5xl lg:text-6xl leading-[1.05]">Discuss your matter, in confidence.</h1>
            <p className="text-zinc-400 text-lg mt-8 leading-relaxed max-w-md">
              Enquiries from lawyers, counsel and principals are handled personally and with complete discretion.
            </p>
            <div className="mt-12 space-y-5">
              <a href="mailto:paul@tactisresolution.co.uk" className="flex items-center gap-4 text-zinc-300 hover:text-white transition-colors">
                <span className="w-11 h-11 rounded-full border border-white/12 flex items-center justify-center"><Mail size={18} strokeWidth={1.5} /></span>
                paul@tactisresolution.co.uk
              </a>
              <a href="https://www.tactisresolution.co.uk" className="flex items-center gap-4 text-zinc-300 hover:text-white transition-colors">
                <span className="w-11 h-11 rounded-full border border-white/12 flex items-center justify-center"><Globe size={18} strokeWidth={1.5} /></span>
                www.tactisresolution.co.uk
              </a>
              <a href="tel:+447980285358" data-testid="contact-call-link" className="flex items-center gap-4 text-zinc-300 hover:text-white transition-colors">
                <span className="w-11 h-11 rounded-full border border-white/12 flex items-center justify-center"><Phone size={18} strokeWidth={1.5} /></span>
                07980 285358
              </a>
              <div className="flex items-center gap-4 text-zinc-300">
                <span className="w-11 h-11 rounded-full border border-white/12 flex items-center justify-center"><MapPin size={18} strokeWidth={1.5} /></span>
                United Kingdom · By appointment
              </div>
            </div>
          </motion.div>

          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl border border-white/10 bg-[#0b0b0b] p-8 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <input required data-testid="contact-name" className={inputCls} placeholder="Full name" value={form.name} onChange={set("name")} />
                <input required type="email" data-testid="contact-email" className={inputCls} placeholder="Email" value={form.email} onChange={set("email")} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-phone" className={inputCls} placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} />
                <input data-testid="contact-subject" className={inputCls} placeholder="Subject" value={form.subject} onChange={set("subject")} />
              </div>
              <textarea required data-testid="contact-message" rows={6} className={inputCls} placeholder="How can we help?" value={form.message} onChange={set("message")} />
              <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-metal w-full py-4 rounded-lg font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? "Sending…" : <>Send enquiry <Send size={15} /></>}
              </button>
              <p className="text-zinc-600 text-xs text-center">Submissions are confidential and reviewed personally.</p>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
